import { useState } from 'react'
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, Typography, TextField, Chip, CircularProgress 
} from '@mui/material'
import { Quiz } from '@mui/icons-material'

export default function QuizModal({ 
  open, 
  questions = [], 
  quizType = 'pre', 
  onSubmit, 
}) {
  const [answers, setAnswers] = useState(
    questions.reduce((acc, q) => {
      acc[q.question_number] = { answer: '', dont_know: false }
      return acc
    }, {})
  )
  const [submitting, setSubmitting] = useState(false)

  // Reset answers when questions change
  useState(() => {
    if (questions.length > 0) {
      setAnswers(
        questions.reduce((acc, q) => {
          acc[q.question_number] = { answer: '', dont_know: false }
          return acc
        }, {})
      )
    }
  }, [questions])

  const handleAnswerChange = (questionNumber, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionNumber]: { answer: value, dont_know: false }
    }))
  }

  const handleDontKnow = (questionNumber) => {
    setAnswers(prev => ({
      ...prev,
      [questionNumber]: { 
        answer: '', 
        dont_know: !prev[questionNumber]?.dont_know 
      }
    }))
  }

  const allAnswered = questions.every(q => {
    const a = answers[q.question_number]
    return a && (a.dont_know || a.answer.trim().length > 0)
  })

  const handleSubmit = async () => {
    if (!allAnswered) return
    setSubmitting(true)
    try {
      const responses = questions.map(q => ({
        question_number: q.question_number,
        answer: answers[q.question_number]?.answer || null,
        dont_know: answers[q.question_number]?.dont_know || false,
      }))
      await onSubmit(responses)
    } catch (err) {
      console.error('Quiz submit error:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const title = quizType === 'pre' 
    ? 'Before You Start' 
    : 'Before You Label'

  const subtitle = quizType === 'pre'
    ? 'Answer these questions to the best of your ability before starting the task.'
    : 'Answer these questions again now that you\'ve completed the task.'

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxWidth: '1000px' } }}
      BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' } }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Quiz sx={{ color: '#3b82f6', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 3, mt: 0 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {questions.map((q, index) => {
            const a = answers[q.question_number] || { answer: '', dont_know: false }
            
            return (
              <Box key={q.question_number}>
                {/* Question */}
                <Typography 
                  variant="body2" 
                  fontWeight={600} 
                  sx={{ color: '#111827', mb: 1 }}
                >
                  {index + 1}. {q.question_text}
                </Typography>

                {/* Answer input */}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  placeholder="Type your answer..."
                  value={a.dont_know ? '' : a.answer}
                  onChange={(e) => handleAnswerChange(q.question_number, e.target.value)}
                  disabled={a.dont_know}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      backgroundColor: a.dont_know ? '#f9fafb' : '#fff',
                    }
                  }}
                />

                {/* Don't know button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Chip
                    label="I don't know"
                    onClick={() => handleDontKnow(q.question_number)}
                    variant={a.dont_know ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      borderColor: a.dont_know ? '#3b82f6' : '#d1d5db',
                      bgcolor: a.dont_know ? '#3b82f6' : 'transparent',
                      color: a.dont_know ? '#fff' : '#6b7280',
                      fontSize: '0.75rem',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        bgcolor: a.dont_know ? '#2563eb' : '#f8fafc',
                      },
                    }}
                  />
                </Box>
              </Box>
            )
          })}
        </Box>
      </DialogContent>

      <Box sx={{ borderTop: '1px solid #e5e7eb', mx: 3 }} />
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            '&:disabled': { bgcolor: '#e5e7eb' },
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
          }}
        >
          {submitting 
            ? 'Submitting...' 
            : quizType === 'pre' 
              ? 'Submit & Start Task' 
              : 'Submit & Continue'
          }
        </Button>
      </DialogActions>
    </Dialog>
  )
}